import type { SQDCEventActionTypeFieldType } from "@/graphql";

export type IshikawaAreaConfig = {
  id: string;
  label: string;
};

export type IshikawaProblemSource = {
  rowIndex: number;
  columnIndex: number;
};

export type IshikawaFieldConfig = {
  areas: IshikawaAreaConfig[];
  problemSource: IshikawaProblemSource | null;
};

export const MAX_ISHIKAWA_AREAS = 10;

const DEFAULT_AREA_DEFINITIONS = [
  ["HUMAN", "Mensch"],
  ["MACHINE", "Maschine"],
  ["METHOD", "Methode"],
  ["MATERIAL", "Material"],
  ["ENVIRONMENT", "Mitwelt"],
] as const;

export function createDefaultIshikawaConfig(
  _translate: (key: string) => unknown,
): IshikawaFieldConfig {
  return {
    areas: DEFAULT_AREA_DEFINITIONS.map(([id, label]) => ({
      id,
      label,
    })),
    problemSource: null,
  };
}

export function normalizeIshikawaConfig(
  value: unknown,
  translate: (key: string) => unknown,
): IshikawaFieldConfig {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return createDefaultIshikawaConfig(translate);
  }

  const config = value as Record<string, unknown>;
  const areas = Array.isArray(config.areas)
    ? config.areas.flatMap((entry): IshikawaAreaConfig[] => {
        if (entry == null || typeof entry !== "object" || Array.isArray(entry)) return [];
        const area = entry as Record<string, unknown>;
        return typeof area.id === "string" && typeof area.label === "string"
          ? [{ id: area.id, label: area.label }]
          : [];
      })
    : [];
  const source = config.problemSource;
  const problemSource =
    source != null && typeof source === "object" && !Array.isArray(source)
      ? (source as Record<string, unknown>)
      : undefined;

  return {
    areas:
      areas.length > 0
        ? areas.slice(0, MAX_ISHIKAWA_AREAS)
        : createDefaultIshikawaConfig(translate).areas,
    problemSource:
      Number.isInteger(problemSource?.rowIndex) && Number.isInteger(problemSource?.columnIndex)
        ? {
            rowIndex: Number(problemSource?.rowIndex),
            columnIndex: Number(problemSource?.columnIndex),
          }
        : null,
  };
}

export type IshikawaSourceField = {
  type: SQDCEventActionTypeFieldType;
  rowIndex: number;
  columnIndex: number;
};

export function isConfiguredProblemSource(
  field: IshikawaSourceField,
  source: IshikawaProblemSource | null,
) {
  return field.rowIndex === source?.rowIndex && field.columnIndex === source.columnIndex;
}
